import { SORT_ORDER } from '@evinced-private/ui-common';

import { TPropertiesInfoSortOptions, TSortOption } from './DashboardTypes';

export enum PROPERTIES_INFO_TYPES {
	PROPERTY_VIEW_NAME = 'PropertyView',
	SCORE_LIFT = 'scoreLift',
	SCORE = 'score',
	SCORE_100 = 'score_100',
	CRITICAL_ISSUES_COUNT = 'criticalIssuesCount',
	PAGES_COUNT = 'pagesCount'
}

const PROPERTIES_INFO_COLUMNS_LABELS = {
	[PROPERTIES_INFO_TYPES.PROPERTY_VIEW_NAME]: 'Property / View',
	[PROPERTIES_INFO_TYPES.SCORE_LIFT]: 'Score Change',
	[PROPERTIES_INFO_TYPES.SCORE]: 'Score',
	[PROPERTIES_INFO_TYPES.SCORE_100]: 'Score / 100',
	[PROPERTIES_INFO_TYPES.CRITICAL_ISSUES_COUNT]: 'Critical Issues',
	[PROPERTIES_INFO_TYPES.PAGES_COUNT]: 'Pages Monitored'
};

const getOptionValue = (key, order): string => `${key}${order}`;

// `criticalLabel` lets the tenant's renamed CRITICAL severity flow through the dashboard
// labels (column header + sort dropdown) without forking these helpers per call site.
export const getLabel = (key, order = null, criticalLabel?: string): string => {
	const baseLabel = PROPERTIES_INFO_COLUMNS_LABELS[key];
	const optionLabel =
		key === PROPERTIES_INFO_TYPES.CRITICAL_ISSUES_COUNT && criticalLabel
			? `${criticalLabel} Issues`
			: baseLabel;
	switch (order) {
		case SORT_ORDER.DESC:
			return `${optionLabel} (High to low)`;
		case SORT_ORDER.ASC:
			return `${optionLabel} (Low to high)`;
		default:
			return optionLabel;
	}
};
const getPropertySortOption = (
	optionVal: PROPERTIES_INFO_TYPES,
	optionOrder: SORT_ORDER,
	criticalLabel?: string
): TSortOption => {
	return {
		sortBy: optionVal,
		orderBy: optionOrder,
		value: getOptionValue(optionVal, optionOrder),
		label: getLabel(optionVal, optionOrder, criticalLabel)
	};
};

export enum KPIS {
	AVERAGE_SCORE_KPI = 'averageScoreKpi',
	TENANT_CRITICAL_ISSUES_KPI = 'tenantCriticalIssuesKpi',
	PAGES_MONITORED_KPI = 'pagesMonitoredKpi'
}

export const DEFAULT_SORT_OPTION_VALUES = {
	sortBy: PROPERTIES_INFO_TYPES.SCORE_LIFT,
	orderBy: SORT_ORDER.ASC
};

export const DEFAULT_SORT_OPTION = {
	sortBy: DEFAULT_SORT_OPTION_VALUES.sortBy,
	orderBy: DEFAULT_SORT_OPTION_VALUES.orderBy,
	value: getOptionValue(DEFAULT_SORT_OPTION_VALUES.sortBy, DEFAULT_SORT_OPTION_VALUES.orderBy),
	label: getLabel(DEFAULT_SORT_OPTION_VALUES.sortBy, DEFAULT_SORT_OPTION_VALUES.orderBy)
};

const PROPERTIES_INFO_SORT_OPTION_DEFINITIONS: {
	value: PROPERTIES_INFO_TYPES;
	order: SORT_ORDER;
}[] = [
	{ value: PROPERTIES_INFO_TYPES.SCORE_LIFT, order: SORT_ORDER.DESC },
	{ value: PROPERTIES_INFO_TYPES.SCORE_LIFT, order: SORT_ORDER.ASC },
	{ value: PROPERTIES_INFO_TYPES.SCORE, order: SORT_ORDER.DESC },
	{ value: PROPERTIES_INFO_TYPES.SCORE, order: SORT_ORDER.ASC },
	{ value: PROPERTIES_INFO_TYPES.CRITICAL_ISSUES_COUNT, order: SORT_ORDER.DESC },
	{ value: PROPERTIES_INFO_TYPES.CRITICAL_ISSUES_COUNT, order: SORT_ORDER.ASC }
];

export const buildPropertiesInfoSortOptions = (
	criticalLabel?: string
): TPropertiesInfoSortOptions =>
	PROPERTIES_INFO_SORT_OPTION_DEFINITIONS.map(({ value, order }) =>
		getPropertySortOption(value, order, criticalLabel)
	);

export enum SCORE_LEVELS {
	EXCELLENT = 'EXCELLENT',
	GOOD = 'GOOD',
	MODERATE = 'MODERATE',
	POOR = 'POOR'
}

export enum PERFORMANCE_OVER_TIME_CHART_TYPE {
	SCORE = 'score',
	CRITICAL_ISSUES = 'criticalIssues',
	TOTAL_ISSUES = 'totalIssues'
}
